import type { ReactNode } from 'react';

import {
  Box,
  IconButton,
  Paper,
  Skeleton,
  Tooltip,
  Typography,
} from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { Check, ExternalLink, Pencil, Share2 } from 'lucide-react';

import {
  BANNER_HEIGHT,
  bannerIconButtonSx as iconButtonSx,
} from '@modules/_shared/components/bannerCard';
import { CoverPhoto } from '@modules/_shared/components/CoverPhoto';
import { ProfileCompletionScore } from '@modules/_shared/components/profile-completion/ProfileCompletionScore';
import type { ProfileCompletionResult } from '@modules/_shared/utils/profileCompletionScore';
import { useLocale } from '@shared/hooks/locale';

type ProfileBannerCardProps = {
  coverPhotoUrl?: string | null;
  avatar: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
};

type ProfileBannerActionButtonsProps = {
  profilePageUrl: string | null;
  editLabel: string;
  canEdit: boolean;
  onEdit?: () => void;
  shareLabel?: string;
  canShare?: boolean;
  onShare?: () => void;
  sx?: SxProps<Theme>;
};

type ProfileCompletionStatusProps = {
  completionResult: ProfileCompletionResult | null | undefined;
  profileType: 'agent' | 'agency';
  onEdit?: () => void;
};

export const profileBannerAvatarSx = {
  width: 100,
  height: 100,
  border: '3px solid',
  borderColor: 'background.paper',
  boxShadow: '0px 2px 6px rgba(0, 0, 0, 0.12)',
  borderRadius: '12px',
  bgcolor: 'background.paper',
  flexShrink: 0,
} as const;

export const ProfileBannerSkeleton = () => (
  <Skeleton
    variant="rounded"
    sx={{ width: '100%', height: BANNER_HEIGHT, borderRadius: '10px' }}
  />
);

export const ProfileBannerActionButtons = ({
  profilePageUrl,
  editLabel,
  canEdit,
  onEdit,
  shareLabel,
  canShare = false,
  onShare,
  sx,
}: ProfileBannerActionButtonsProps) => {
  const { t } = useLocale();

  return (
    <Box sx={{ display: 'flex', gap: 0.5, ...sx }}>
      <Tooltip title={t('View profile page')}>
        <span>
          <IconButton
            size="small"
            component="a"
            href={profilePageUrl ?? '#'}
            target="_blank"
            rel="noopener noreferrer"
            disabled={!profilePageUrl}
            sx={iconButtonSx}
          >
            <ExternalLink size={18} />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={editLabel}>
        <span>
          <IconButton
            size="small"
            disabled={!canEdit || !onEdit}
            onClick={onEdit}
            sx={iconButtonSx}
          >
            <Pencil size={18} />
          </IconButton>
        </span>
      </Tooltip>
      {shareLabel && (
        <Tooltip title={shareLabel}>
          <span>
            <IconButton
              size="small"
              disabled={!canShare || !onShare}
              onClick={onShare}
              sx={iconButtonSx}
            >
              <Share2 size={18} />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Box>
  );
};

export const ProfileCompletionStatus = ({
  completionResult,
  profileType,
  onEdit,
}: ProfileCompletionStatusProps) => {
  const { t } = useLocale();

  if (!completionResult) {
    return null;
  }

  if (completionResult.score === 100) {
    return (
      <Typography
        variant="caption"
        sx={{
          color: 'success.main',
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
        }}
      >
        <Check size={14} strokeWidth={2.5} />
        {t('Profile complete')}
      </Typography>
    );
  }

  return (
    <ProfileCompletionScore
      completionResult={completionResult}
      size="small"
      profileType={profileType}
      onEdit={onEdit}
    />
  );
};

export const ProfileBannerCard = ({
  coverPhotoUrl,
  avatar,
  children,
  footer,
}: ProfileBannerCardProps) => (
  <Paper
    elevation={0}
    sx={{
      width: '100%',
      overflow: 'hidden',
      borderRadius: '10px',
      border: '1px solid',
      borderColor: 'grey.200',
      position: 'relative',
    }}
  >
    <CoverPhoto
      src={coverPhotoUrl}
      width={1400}
      sx={{ height: BANNER_HEIGHT, position: 'relative' }}
    />

    <Box
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'flex-end' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1, sm: 2 },
        px: { xs: 2, sm: 3 },
        pb: 2.5,
        mt: '-50px',
        position: 'relative',
      }}
    >
      {avatar}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          flex: 1,
          minWidth: 0,
          width: '100%',
          pt: { xs: 0, sm: '54px' },
          pb: 0.5,
          gap: { xs: 1, sm: 0 },
        }}
      >
        {children}
      </Box>
    </Box>

    {footer}
  </Paper>
);
